import { Calendar, Loader2 } from 'lucide-react';
import type { RangePreset } from './analytics';

const PRESETS: { id: RangePreset; label: string }[] = [
  { id: 'month', label: 'This month' },
  { id: 'year', label: 'Year' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom' },
];

const selectClass =
  'rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500';

export const MetricsToolbar: React.FC<{
  preset: RangePreset;
  onPreset: (p: RangePreset) => void;
  year: number;
  onYear: (y: number) => void;
  minYear: number;
  maxYear: number;
  custom: { start: string; end: string };
  onCustom: (c: { start: string; end: string }) => void;
  rangeLabel: string;
  /** Category/tier options come from the loaded window, so they stay in sync. */
  categories: string[];
  category: string;
  onCategory: (c: string) => void;
  tiers: string[];
  tier: string;
  onTier: (t: string) => void;
  isFetching?: boolean;
}> = ({
  preset,
  onPreset,
  year,
  onYear,
  minYear,
  maxYear,
  custom,
  onCustom,
  rangeLabel,
  categories,
  category,
  onCategory,
  tiers,
  tier,
  onTier,
  isFetching,
}) => (
  <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
    <div className="flex items-center gap-2 text-sm text-gray-500">
      <Calendar className="h-4 w-4 text-gray-400" />
      <span className="font-medium text-gray-700">{rangeLabel}</span>
      {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <select className={selectClass} value={category} onChange={(e) => onCategory(e.target.value)}>
        <option value="">All categories</option>
        {categories.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>

      <select className={selectClass} value={tier} onChange={(e) => onTier(e.target.value)}>
        <option value="">All tiers</option>
        {tiers.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>

      {preset === 'year' && (
        <div className="inline-flex items-center rounded-md border border-gray-200 bg-white">
          <button
            type="button"
            onClick={() => onYear(year - 1)}
            disabled={year <= minYear}
            className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-40"
            aria-label="Previous year"
          >
            ‹
          </button>
          <span className="min-w-[3rem] px-1 text-center text-xs font-medium text-gray-700">{year}</span>
          <button
            type="button"
            onClick={() => onYear(year + 1)}
            disabled={year >= maxYear}
            className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-40"
            aria-label="Next year"
          >
            ›
          </button>
        </div>
      )}

      {preset === 'custom' && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <input
            type="date"
            value={custom.start}
            max={custom.end || undefined}
            onChange={(e) => onCustom({ ...custom, start: e.target.value })}
            className="rounded-md border border-gray-200 px-2 py-1 text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <span>→</span>
          <input
            type="date"
            value={custom.end}
            min={custom.start || undefined}
            onChange={(e) => onCustom({ ...custom, end: e.target.value })}
            className="rounded-md border border-gray-200 px-2 py-1 text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      )}

      <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPreset(p.id)}
            className={`px-3 py-1.5 text-xs font-medium ${
              preset === p.id ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);
